package scroll

import ext.*
import rendering.RendererManager
import rendering.SceneManager
import three.js.Group
import util.DebugManager

private const val INTRO_TIMEFRAME = 0.3
private const val OUTRO_TIMEFRAME = 0.3

object ScrollAnimator {
    private val group = Group().apply {
        SceneManager.scene.add(this)
    }

    fun animate(
        animation: ScrollAnimation,
        scrollProgress: Double
    ) {
        group.clear()
        if (RendererManager.renderer.xr.isPresenting) {
            // move stuff up and to the front a bit when in AR
            group.position.y = 1500
            group.position.z = -500
        }

        val previousKeyframe = animation.last { it.progress.start <= scrollProgress }
        val nextKeyframe = animation.firstOrNull { it.progress.endInclusive > scrollProgress }

        DebugManager.debug("Scroll progress: $scrollProgress")
        DebugManager.debug("Previous keyframe: ${previousKeyframe.progress}")
        DebugManager.debug("Next keyframe: ${nextKeyframe?.progress}")

        handleOldElements(previousKeyframe, nextKeyframe, scrollProgress)
        nextKeyframe?.let {
            handleNewElements(previousKeyframe, it, scrollProgress)
            handlePersistedElements(previousKeyframe, nextKeyframe, scrollProgress)
        }

        DebugManager.debug("Group children: ${group.children.size}")
        DebugManager.debug("Unique elements: ${group.children.distinct().size}")
    }

    /**
     * Draw the elements that are present in the previous keyframe *and* in the next keyframe.
     */
    private fun handlePersistedElements(previousKeyframe: ScrollKeyframe, nextKeyframe: ScrollKeyframe, scrollProgress: Double) {
        val persistedElements = previousKeyframe.elements.filter { it in nextKeyframe.elements }
        DebugManager.debug("Persisted elements: ${persistedElements.size}")

        val startProgress = previousKeyframe.progress.endInclusive
        val destProgress = nextKeyframe.progress.start

        persistedElements.forEach {
            val startPosition = it.pos + previousKeyframe.globalTransform
            val destPosition = nextKeyframe.elements.first { e -> e == it }.pos + nextKeyframe.globalTransform
            val interpolatedPosition =
                startPosition + (destPosition - startPosition) * (scrollProgress - startProgress) / (destProgress - startProgress)

            val startRotation = it.rot + previousKeyframe.globalRotation
            val destRotation = nextKeyframe.elements.first { e -> e == it }.rot + nextKeyframe.globalRotation
            val interpolatedRotation =
                startRotation + (destRotation - startRotation) * (scrollProgress - startProgress) / (destProgress - startProgress)
            group.add(it.instantiate().apply {
                position.set(interpolatedPosition)
                rotation.set(interpolatedRotation)
            })
        }
    }

    /**
     * Draw the "new elements", if necessary. New elements are elements from the next keyframe
     * that are not persisted. They have an intro animation timeframe of 0.3 viewport heights.
     * Before that, they are not rendered.
     */
    private fun handleNewElements(
        previousKeyframe: ScrollKeyframe, nextKeyframe: ScrollKeyframe, scrollProgress: Double
    ) {
        val destProgress = nextKeyframe.progress.start
        if (destProgress - INTRO_TIMEFRAME > scrollProgress) {
            // intro animation timeframe is not reached yet
            return
        }

        // render intro animation for the elements from next keyframe
        val newElements = nextKeyframe.elements.filter { it !in previousKeyframe.elements }
        DebugManager.debug("New elements: ${newElements.size}")

        newElements.forEach { elt ->
            val startPosition = elt.pos + elt.introOffset + nextKeyframe.globalTransform
            val destPosition = elt.pos + nextKeyframe.globalTransform
            val interpolatedPosition =
                startPosition + (destPosition - startPosition) * (scrollProgress - (destProgress - INTRO_TIMEFRAME)) / INTRO_TIMEFRAME
            group.add(elt.instantiate().apply {
                position.set(interpolatedPosition)
                // todo rotation support for intro animation
            })
        }
    }

    /**
     * Draw the "old elements", if necessary. Old elements are elements from the previous
     * keyframe that are not persisted. They have an outro animation timeframe of 0.3 viewport heights.
     * After that, they are no longer rendered.
     */
    private fun handleOldElements(previousKeyframe: ScrollKeyframe, nextKeyframe: ScrollKeyframe?, scrollProgress: Double) {
        val startProgress = previousKeyframe.progress.endInclusive

        if (startProgress + OUTRO_TIMEFRAME < scrollProgress) {
            // outro animation timeframe is over
            return
        }

        // render outro animation for the elements from previous keyframe
        val oldElements = previousKeyframe.elements.filter { it !in (nextKeyframe?.elements ?: emptyList()) }
        DebugManager.debug("Old elements: ${oldElements.size}")

        oldElements.forEach { elt ->
            val startPosition = elt.pos + previousKeyframe.globalTransform
            val destPosition = elt.pos + elt.outroOffset + previousKeyframe.globalTransform
            val interpolatedPosition =
                startPosition + (destPosition - startPosition) * (scrollProgress - startProgress) / OUTRO_TIMEFRAME
            group.add(elt.instantiate().apply {
                position.set(interpolatedPosition)
                // todo rotation support for outro animation
            })
        }
    }
}
